import { useEffect, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface RightDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl';
  children: ReactNode;
  footer?: ReactNode;
}

const widthClasses: Record<NonNullable<RightDrawerProps['width']>, string> = {
  sm: 'w-full sm:w-96',
  md: 'w-full sm:w-[28rem]',
  lg: 'w-full sm:w-[36rem]',
  xl: 'w-full sm:w-[48rem]',
};

export function RightDrawer({
  open,
  onClose,
  title,
  width = 'md',
  children,
  footer,
}: RightDrawerProps) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div
        aria-hidden="true"
        className="flex-1 bg-zinc-900/40 transition-opacity"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        className={cn(
          'flex flex-col bg-white shadow-xl ring-1 ring-zinc-200',
          widthClasses[width],
        )}
      >
        {title && (
          <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
            <h2 className="text-base font-semibold text-zinc-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-zinc-500 hover:bg-zinc-100"
              aria-label="Close"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 6L18 18M6 18L18 6" />
              </svg>
            </button>
          </header>
        )}
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer && (
          <footer className="border-t border-zinc-200 px-4 py-3">{footer}</footer>
        )}
      </aside>
    </div>
  );
}
